import { useEffect, useState, useCallback } from "react";
import { Card } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

function SiteInformation({ siteId, timestamp }) {
  const [lastUpdate, setLastUpdate] = useState("");
  const [siteAddress, setSiteAddress] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
  
    try {
      const [datePart, timePart] = timestamp.split(',');
      const [day, month, year] = datePart.split('/');
      const isoTimestamp = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart.trim()}Z`;
      const date = new Date(isoTimestamp);
  
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", timestamp);
        return timestamp;
      }
  
      return date.toLocaleString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: "short",
      });
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return timestamp;
    }
  };

  const initializeMap = useCallback(() => {
    if (siteAddress && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: siteAddress }, (results, status) => {
        if (status === "OK") {
          const mapElement = document.getElementById("map");
          if (!mapElement) return;
          
          // Ensure map element is visible
          mapElement.style.display = "block";
          
          const map = new window.google.maps.Map(mapElement, {
            center: results[0].geometry.location,
            zoom: 10,
            mapTypeId: "satellite",
            gestureHandling: "cooperative", // Better mobile interaction
          });

          new window.google.maps.Marker({
            position: results[0].geometry.location,
            map: map,
            title: `Site Location: ${siteAddress}`,
          });
        } else {
          console.error("Geocode was not successful:", status);
          setMapError(true);
        }
      });
    }
  }, [siteAddress]);

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const response = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${siteId}`
        );
        if (!response.ok) throw new Error("Failed to fetch site data");
        const data = await response.json();
        setSiteAddress(`${data.street}, ${data.address_postcode}`);
      } catch (error) {
        console.error("Error fetching site data:", error);
      }
    };

    fetchSiteData();
  }, [siteId]);

  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBlE514dTiL8nQSdTid4KrHfhI-U4lDR80`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      script.onerror = () => setMapError(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (siteAddress && mapLoaded) {
      initializeMap();
    }
  }, [siteAddress, mapLoaded, initializeMap]);

  useEffect(() => {
    const updateLastUpdate = () => {
      setLastUpdate(formatTimestamp(timestamp));
    };

    updateLastUpdate();
    const intervalId = setInterval(updateLastUpdate, 60000);
    return () => clearInterval(intervalId);
  }, [timestamp]);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <VuiBox display="flex" flexDirection="column" height="100%" p={2}>
        <VuiTypography variant="lg" color="white" fontWeight="bold">
          Site Information
        </VuiTypography>
        <VuiTypography variant="caption" color="text" p={1}>
          Last Update: {lastUpdate}
        </VuiTypography>

        <VuiBox
          id="map"
          sx={{
            flex: 1,
            width: "100%",
            borderRadius: "10px",
            overflow: "hidden",
            minHeight: "100px", // Ensure minimum height
            height: {
              xs: "300px", // Increased mobile height
              sm: "350px",
              md: "400px",
              lg: "450px",
              xl: "500px",
            },
            display: mapLoaded && siteAddress ? "block" : "none", // Hide until ready
            backgroundColor: "#121212", // Fallback background
          }}
        >
          {mapError && (
            <VuiTypography color="error">
              Failed to load map. Please check your connection.
            </VuiTypography>
          )}
          {!mapLoaded && !mapError && (
            <VuiTypography color="text">
              Loading map...
            </VuiTypography>
          )}
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default SiteInformation;